import { forwardRef, MutableRefObject, Ref } from 'react';
import { Input } from 'antd';
import {
  buildInputClassName,
  inputAdornmentClassName,
  inputHelperClassName,
  inputLabelClassName,
} from '../formControlStyles';
import { TextFieldProps } from './types';

function assignRef(ref: Ref<unknown> | undefined, value: unknown) {
  if (!ref) {
    return;
  }

  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  (ref as MutableRefObject<unknown>).current = value;
}

function sanitizeInputProps(inputProps?: TextFieldProps['inputProps']) {
  if (!inputProps) {
    return {};
  }

  const { className, ...rest } = inputProps;
  delete rest.sx;

  return { rest, className };
}

export const TextField = forwardRef<HTMLInputElement | HTMLTextAreaElement, TextFieldProps>(function TextField(
  {
    helperText,
    error = false,
    label,
    className,
    fullWidth = false,
    multiline = false,
    rows,
    inputRef,
    inputProps,
    InputProps,
    placeholder,
    suffix,
    prefix,
    value,
    id,
    name,
    disabled,
    size = 'middle',
    status,
    variant,
    InputLabelProps,
    onBlur,
    onFocus,
    onChange,
    ...props
  },
  ref,
) {
  void variant;
  void InputLabelProps;
  const normalizedValue = value ?? '';
  const hasValue = String(normalizedValue).length > 0;
  const mergedStatus = error ? 'error' : status;
  const mergedPrefix = InputProps?.startAdornment ?? prefix;
  const mergedSuffix = InputProps?.endAdornment ?? suffix;
  const inputId =
    id ??
    name ??
    (typeof label === 'string' ? label.toLowerCase().replace(/[^a-z0-9]+/g, '-') : undefined) ??
    undefined;
  const { rest: sanitizedInputProps, className: inputPropsClassName } = sanitizeInputProps(inputProps);
  const sharedClassName = buildInputClassName({
    hasError: mergedStatus === 'error',
    isDisabled: disabled,
    hasValue,
    isMultiline: multiline,
    className: [className, inputPropsClassName].filter(Boolean).join(' '),
  });
  const mergedPlaceholder = InputProps?.placeholder ?? placeholder;

  const inputElement = multiline ? (
    <Input.TextArea
      {...props}
      {...sanitizedInputProps}
      rows={rows}
      value={normalizedValue}
      disabled={disabled}
      status={mergedStatus}
      id={inputId}
      name={name}
      placeholder={mergedPlaceholder}
      className={[sharedClassName, fullWidth ? 'w-full' : ''].filter(Boolean).join(' ')}
      ref={ref as Ref<HTMLTextAreaElement>}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={onChange}
    />
  ) : (
    <Input
      {...props}
      {...sanitizedInputProps}
      value={normalizedValue}
      disabled={disabled}
      status={mergedStatus}
      id={inputId}
      name={name}
      placeholder={mergedPlaceholder}
      prefix={mergedPrefix ? <span className={inputAdornmentClassName}>{mergedPrefix}</span> : undefined}
      suffix={<span className={inputAdornmentClassName}>{mergedSuffix}</span>}
      className={[sharedClassName, fullWidth ? 'w-full' : ''].filter(Boolean).join(' ')}
      size={size}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={onChange}
      ref={node => {
        assignRef(ref, node?.input ?? null);
        assignRef(inputRef, node?.input ?? null);
      }}
    />
  );

  return (
    <div className={fullWidth ? 'w-full' : undefined}>
      {label ? (
        <label htmlFor={inputId} className={inputLabelClassName}>
          {label}
        </label>
      ) : null}
      {inputElement}
      {helperText ? <p className={inputHelperClassName}>{helperText}</p> : null}
    </div>
  );
});
